import { Vec3 } from '@/core/math/Vec3';
import { g0 } from '@/core/units';
import { CelestialBody } from '@/universe/CelestialBody';
import {
  circularVelocity,
  elementsFromState,
  isHyperbolic,
  periapsisRadius,
  stateVectorAt,
  timeToPeriapsis,
} from '@/universe/OrbitalElements';
import { orbitalBasisDeltaV } from '../ManeuverPlanner';
import { ChainedPrediction } from '../TrajectoryPredictor';
import { BurnComponents, TransferSolution, TransferSolver, departureDeltaV, sizeTransfer } from '../TransferSolver';
import { BaseMacro, MacroContext, MacroEstimate, MacroStatus } from './Macro';

/** Trajectory evaluations per tick while planning. About 1.1 ms each. */
const SOLVER_SLICE = 1;
/** Cap on each component of the single mid-course correction, m/s. */
const CORRECTION_LIMIT = 200;
/** Evaluation budget for the correction solve. */
const CORRECTION_EVALUATIONS = 120;

/**
 * Flies to another body using real gravity.
 *
 * The whole macro is four physical operations: solve a departure burn, fly it,
 * coast under gravity, and capture. What makes it different from pointing at
 * the target and burning — which is what flew a vessel straight into Halcyon
 * when its moon was selected from the far side — is that the burn is chosen by
 * propagating the actual patched-conic route and rejecting any route that hits
 * something. See `TrajectoryPredictor.predictChained` and `TransferSolver`.
 *
 * Nothing here steers during the coast. Once the departure burn is finished the
 * vessel is on a ballistic trajectory and the macro only watches it, which is
 * the point: the route is whatever gravity does with the state the burn
 * produced, so any bending or assist it picks up is real rather than faked. The
 * one exception is a single mid-course correction, sized in the low hundreds of
 * m/s, which exists because the analytical predictor ignores drag and
 * third-body perturbation and the real integrator does not.
 *
 * Deliberately out of scope, and not attempted: searching for a gravity-assist
 * route, and landing. Arrival ends in a bound orbit about the target, the same
 * way `DockingMacro` ends at capture rather than on the ground.
 */
export class TransferToTargetMacro extends BaseMacro {
  readonly id = 'transferToTarget';
  readonly name = 'Transfer to Target';
  readonly description =
    'Solves a departure burn whose gravity-propagated route reaches the selected body without intersecting ' +
    'anything, flies it, coasts ballistically with one mid-course correction, and captures into orbit.';
  readonly phases = ['Plan', 'Depart', 'Coast', 'Correct', 'Arrive', 'Complete'];

  /** The planned route, for the route preview. Present as soon as one exists. */
  route: ChainedPrediction | null = null;

  private target: CelestialBody | null = null;
  private departureBodyId: string | null = null;
  private solver: TransferSolver | null = null;
  private solution: TransferSolution | null = null;

  /** Delta-v still owed by the burn currently executing, m/s. */
  private burnRemaining = 0;
  private burnComponents: BurnComponents = { prograde: 0, normal: 0, radial: 0 };
  private burnCentreTime = 0;
  /** Phase to return to when the burn in progress finishes. */
  private burnReturnPhase = 2;

  private correctionUsed = false;
  private correctionSolver: TransferSolver | null = null;
  private captureStarted = false;

  // ==========================================================================
  // Preconditions and estimate
  // ==========================================================================

  estimate(ctx: MacroContext): MacroEstimate {
    const { ship, body, target } = ctx;
    const failureModes = [
      'No safe route found within the solver budget',
      'Insufficient delta-v for the departure or capture burn',
      'Propellant exhausted during a burn',
      'Arrival periapsis too low for a capture',
    ];
    const empty = {
      duration: 0,
      propellant: 0,
      deltaV: 0,
      peakAcceleration: 0,
      heatLoad: 0,
      failureModes,
    };

    if (!target) {
      return { ...empty, blocked: true, blockedReason: 'No navigation target selected' };
    }
    if (target.id === body.id) {
      return { ...empty, blocked: true, blockedReason: `Already at ${target.name}` };
    }
    if (ship.contact.landed) {
      return { ...empty, blocked: true, blockedReason: 'Vessel is on the ground; reach orbit first' };
    }

    const sizing = sizeTransfer(ctx.predictor, body, ship.body.position, ship.body.velocity, ctx.t, target);
    const departure = Math.abs(
      departureDeltaV(sizing, body, ship.body.position.length(), ship.body.velocity.length()),
    );
    const deltaV = departure + sizing.captureDeltaV;

    const isp = Math.max(1, ...ship.mainEngines.map((e) => e.def.ispVacuum));
    const mass = ship.massProperties.total;
    const propellant = mass * (1 - Math.exp(-deltaV / (isp * g0)));
    const thrust = ship.mainEngines.reduce((s, e) => s + e.def.maxThrust, 0);
    const available = ship.totalDeltaV();

    return {
      duration: sizing.transferTime,
      propellant,
      deltaV,
      peakAcceleration: thrust / Math.max(1, mass - propellant),
      heatLoad: 0,
      failureModes,
      blocked: deltaV > available || thrust <= 0,
      blockedReason:
        thrust <= 0
          ? 'No main engine available'
          : deltaV > available
            ? `Requires about ${deltaV.toFixed(0)} m/s; ${available.toFixed(0)} m/s available`
            : null,
    };
  }

  protected override onStart(ctx: MacroContext): void {
    this.target = ctx.target;
    this.departureBodyId = ctx.body.id;
    this.correctionUsed = false;
    this.captureStarted = false;
    this.route = null;
    this.solution = null;
    this.solver = this.buildSolver(ctx);
    this.advance(0, `Planning a route to ${this.target?.name ?? 'the target'}`);
  }

  private buildSolver(ctx: MacroContext, correction = false): TransferSolver {
    return new TransferSolver({
      predictor: ctx.predictor,
      referenceBody: ctx.body,
      position: ctx.ship.body.position.clone(),
      velocity: ctx.ship.body.velocity.clone(),
      t0: ctx.t,
      target: this.target!,
      availableDeltaV: ctx.ship.totalDeltaV() * (1 - this.safety.margin * 0.3),
      terrainCeilingFor: ctx.terrainCeilingFor,
      ...(correction
        ? {
            fixedBurnTime: ctx.t + 20,
            componentLimit: CORRECTION_LIMIT,
            maxEvaluations: CORRECTION_EVALUATIONS,
          }
        : {}),
    });
  }

  // ==========================================================================
  // Phases
  // ==========================================================================

  protected tick(ctx: MacroContext): MacroStatus {
    const vitals = this.checkVitals(ctx);
    if (vitals) return this.fail(ctx, vitals);
    if (!this.target) return this.fail(ctx, 'No navigation target selected');

    switch (this.phaseIndex) {
      case 0:
        return this.tickPlan(ctx);
      case 1:
        return this.tickBurn(ctx);
      case 2:
        return this.tickCoast(ctx);
      case 3:
        return this.tickCorrect(ctx);
      case 4:
        return this.tickArrive(ctx);
      default:
        return 'complete';
    }
  }

  /**
   * Planning is spread across ticks rather than blocking: a solve costs a few
   * hundred trajectory evaluations, which is a visible frame hitch if it all
   * happens at once. The provisional best route is published every tick so the
   * route preview can draw a candidate while the search is still improving it.
   */
  private tickPlan(ctx: MacroContext): MacroStatus {
    const solver = this.solver!;
    solver.step(SOLVER_SLICE);
    if (solver.best) this.route = solver.best.route;

    if (solver.status === 'failed') {
      return this.fail(ctx, solver.failureReason ?? 'No safe route found');
    }
    if (solver.status === 'solved') {
      this.solution = solver.best!;
      this.route = this.solution.route;
      this.beginBurn(this.solution.components, this.solution.burnTime, 2);
      this.advance(1, `Departure burn: ${this.solution.deltaV.toFixed(0)} m/s`);
      return 'running';
    }

    const miss = solver.best ? ` (best miss ${(solver.best.miss / 1000).toFixed(0)} km)` : '';
    this.statusText = `Planning: ${solver.evaluations} routes evaluated${miss}`;
    return 'running';
  }

  private tickBurn(ctx: MacroContext): MacroStatus {
    if (ctx.body.id !== this.departureBodyId) {
      return this.fail(ctx, 'Reference frame changed during the departure burn');
    }
    const result = this.stepBurn(ctx, ctx.ship.body.position, ctx.ship.body.velocity);
    if (result === 'failed') return this.fail(ctx, 'Propellant exhausted during the departure burn');
    if (result === 'noThrust') return this.fail(ctx, 'No engine can produce thrust');
    if (result === 'complete') {
      this.releaseBurnControls(ctx);
      this.advance(this.burnReturnPhase, 'Coasting');
      return 'running';
    }
    this.statusText =
      result === 'waiting'
        ? `Holding burn attitude, ${Math.max(0, this.burnCentreTime - ctx.t).toFixed(0)} s to the window`
        : `Departure burn: ${this.burnRemaining.toFixed(0)} m/s remaining`;
    return 'running';
  }

  /**
   * The coast does nothing but watch. That is the whole point of building the
   * route out of real conics: the trajectory between the burns is whatever
   * gravity makes of the state the departure burn produced.
   */
  private tickCoast(ctx: MacroContext): MacroStatus {
    const target = this.target!;
    const separation = this.separationFromTarget(ctx);

    if (ctx.body.id === target.id || separation < target.sphereOfInfluence) {
      this.advance(4, `Arrived at ${target.name}`);
      return 'running';
    }

    // Exactly one correction, at roughly the midpoint of the coast, for the
    // divergence the analytical predictor cannot see (it ignores drag and
    // third-body perturbation by its own doc comment). Not a control loop:
    // re-solving every tick would make the route unpredictable and would spend
    // propellant chasing noise.
    const arrival = this.solution?.route.arrivalTime ?? Infinity;
    const burnEnd = this.solution?.burnTime ?? ctx.t;
    if (!this.correctionUsed && Number.isFinite(arrival) && ctx.t >= burnEnd + (arrival - burnEnd) * 0.5) {
      return this.tickCorrect(ctx);
    }

    const eta = Number.isFinite(arrival) ? (arrival - ctx.t) / 3600 : NaN;
    this.statusText =
      `Coasting to ${target.name}: ${(separation / 1000).toFixed(0)} km` +
      (Number.isFinite(eta) ? `, ${eta.toFixed(1)} h to arrival` : '');
    return 'running';
  }

  /** Solves and then flies the single mid-course correction. */
  private tickCorrect(ctx: MacroContext): MacroStatus {
    if (this.phaseIndex === 3 && this.correctionSolver === null) {
      // Already solved; this is the burn half of the correction.
      const result = this.stepBurn(ctx, ctx.ship.body.position, ctx.ship.body.velocity);
      if (result === 'failed' || result === 'noThrust') {
        // A correction that cannot be flown is not fatal — the uncorrected
        // route was validated safe when it was chosen.
        this.releaseBurnControls(ctx);
        this.advance(2, 'Correction abandoned; coasting on the original route');
        return 'running';
      }
      if (result === 'complete') {
        this.releaseBurnControls(ctx);
        this.advance(2, 'Correction complete');
        return 'running';
      }
      this.statusText = `Mid-course correction: ${this.burnRemaining.toFixed(1)} m/s remaining`;
      return 'running';
    }

    if (!this.correctionSolver) {
      this.correctionSolver = this.buildSolver(ctx, true);
      this.advance(3, 'Solving mid-course correction');
    }

    const solver = this.correctionSolver;
    solver.step(SOLVER_SLICE);
    if (solver.status === 'searching') {
      this.statusText = `Mid-course correction: ${solver.evaluations} routes evaluated`;
      return 'running';
    }

    this.correctionUsed = true;
    this.correctionSolver = null;

    if (solver.status === 'solved' && solver.best) {
      this.solution = solver.best;
      this.route = solver.best.route;
      this.beginBurn(solver.best.components, solver.best.burnTime, 2);
      this.advance(3, `Mid-course correction: ${solver.best.deltaV.toFixed(1)} m/s`);
      return 'running';
    }

    // No better route than the one already being flown. Keep the original.
    this.advance(2, 'No correction needed; coasting on the original route');
    return 'running';
  }

  /**
   * Capture: coast to periapsis about the target and burn retrograde until the
   * orbit is circular there.
   *
   * The macro stops at a bound orbit deliberately. Landing is a separate
   * operation with its own macro and its own failure modes, and stopping at
   * capture is the same boundary `DockingMacro` draws.
   */
  private tickArrive(ctx: MacroContext): MacroStatus {
    const { ship } = ctx;
    const target = this.target!;

    // Wait for the reference frame handover before planning about the target —
    // the vessel's own position and velocity are only expressed in the target's
    // frame once the game has rebased it, which happens on SOI entry.
    if (ctx.body.id !== target.id) {
      this.statusText = `Approaching ${target.name}: ${(this.separationFromTarget(ctx) / 1000).toFixed(0)} km`;
      return 'running';
    }

    if (this.captureStarted) {
      const result = this.stepBurn(ctx, ship.body.position, ship.body.velocity);
      if (result === 'failed') return this.fail(ctx, 'Propellant exhausted during the capture burn');
      if (result === 'noThrust') return this.fail(ctx, 'No engine can produce thrust');
      if (result === 'complete') {
        this.releaseBurnControls(ctx);
        this.releaseControls(ctx);
        const elements = elementsFromState(ship.body.position, ship.body.velocity, target.mu, ctx.t);
        const altitude = (periapsisRadius(elements) - target.radius) / 1000;
        this.advance(5, 'Complete');
        this.statusText = `Captured at ${target.name}, periapsis ${altitude.toFixed(0)} km`;
        return 'complete';
      }
      this.statusText =
        result === 'waiting'
          ? `Coasting to capture burn, ${Math.max(0, this.burnCentreTime - ctx.t).toFixed(0)} s`
          : `Capture burn: ${this.burnRemaining.toFixed(0)} m/s remaining`;
      return 'running';
    }

    const elements = elementsFromState(ship.body.position, ship.body.velocity, target.mu, ctx.t);
    const rPeriapsis = periapsisRadius(elements);
    if (rPeriapsis < target.radius * 1.02) {
      return this.fail(ctx, `Arrival periapsis is inside ${target.name}; take manual control`);
    }

    if (!isHyperbolic(elements) && ship.body.position.length() < target.sphereOfInfluence * 0.5) {
      // Already bound and not on the way out: nothing left to capture.
      this.releaseControls(ctx);
      this.advance(5, 'Complete');
      this.statusText = `In orbit at ${target.name}, periapsis ${((rPeriapsis - target.radius) / 1000).toFixed(0)} km`;
      return 'complete';
    }

    const dtPeriapsis = timeToPeriapsis(elements, ctx.t);
    if (!Number.isFinite(dtPeriapsis)) {
      return this.fail(ctx, `Past periapsis at ${target.name} on an escape path; take manual control`);
    }

    // At periapsis the velocity is purely horizontal, so circularising is a
    // pure retrograde change — the same argument `planCircularisation` makes.
    const atPeriapsis = stateVectorAt(elements, ctx.t + dtPeriapsis);
    const capture = circularVelocity(target.mu, rPeriapsis) - atPeriapsis.velocity.length();
    if (Math.abs(capture) > ship.totalDeltaV()) {
      return this.fail(
        ctx,
        `Capture needs ${Math.abs(capture).toFixed(0)} m/s; ${ship.totalDeltaV().toFixed(0)} m/s available`,
      );
    }

    this.captureStarted = true;
    this.beginBurn({ prograde: capture, normal: 0, radial: 0 }, ctx.t + dtPeriapsis, 4);
    this.statusText = `Capture burn planned: ${Math.abs(capture).toFixed(0)} m/s at periapsis`;
    return 'running';
  }

  // ==========================================================================
  // Burn execution
  // ==========================================================================

  private beginBurn(components: BurnComponents, centreTime: number, returnPhase: number): void {
    this.burnComponents = { ...components };
    this.burnCentreTime = centreTime;
    this.burnRemaining = Math.hypot(components.prograde, components.normal, components.radial);
    this.burnReturnPhase = returnPhase;
  }

  /**
   * One step of a burn, following `ManeuverPlanner.updateExecution`'s pattern:
   * hold attitude until the window opens, then integrate the delta-v actually
   * delivered from the thrust the vessel is really producing, and taper the
   * throttle at the end so the burn cannot overshoot by a control period's
   * worth of acceleration.
   *
   * The burn is driven straight into the flight computer rather than through a
   * `ManeuverNode` on purpose: a macro's internal burns are not the player's
   * plan and should not appear in the manoeuvre list they can edit. The
   * *sizing* still goes through `ManeuverPlanner.estimate`, which is pure, so
   * the burn window is computed by the same code that computes it for a node.
   *
   * `basisPosition`/`basisVelocity` say which body the burn is planned about.
   * Only their direction matters downstream, and every inertial frame here
   * shares an orientation, so a direction derived in the target's frame is
   * equally valid handed to a vessel still referenced to its parent.
   */
  private stepBurn(
    ctx: MacroContext,
    basisPosition: Vec3,
    basisVelocity: Vec3,
  ): 'waiting' | 'burning' | 'complete' | 'failed' | 'noThrust' {
    const { ship, computer, planner, t, dt } = ctx;
    const c = this.burnComponents;

    const direction = orbitalBasisDeltaV(basisPosition, basisVelocity, c.prograde, c.normal, c.radial);
    const length = direction.length();
    if (length < 1e-6) return 'complete';
    direction.mulScalar(1 / length);

    const estimate = planner.estimate(
      { id: 'transfer-internal', time: this.burnCentreTime, ...c, label: 'Transfer' },
      ship,
    );
    if (estimate.noThrust) return 'noThrust';

    computer.maneuverDirection = direction;
    computer.setAlignment('maneuver');

    if (t < this.burnCentreTime - estimate.duration / 2) {
      ship.controls.throttle = 0;
      ship.controls.mainEngineArmed = false;
      return 'waiting';
    }

    const acceleration = ship.lastThrustTotal / Math.max(1, ship.massProperties.total);
    const alignment = Math.max(0, ship.body.forward().dot(direction));
    this.burnRemaining -= acceleration * alignment * dt;

    if (this.burnRemaining <= 0.05) return 'complete';
    if (ship.massProperties.fuel <= 0.5) return 'failed';

    const stopping = acceleration * dt * 4;
    ship.controls.mainEngineArmed = true;
    ship.controls.throttle =
      this.burnRemaining < stopping ? Math.max(0.08, this.burnRemaining / Math.max(1e-3, stopping)) : 1;
    return 'burning';
  }

  private releaseBurnControls(ctx: MacroContext): void {
    ctx.ship.controls.throttle = 0;
    ctx.ship.controls.mainEngineArmed = false;
    this.burnRemaining = 0;
  }

  /** Distance from the vessel to the target, whatever frames they are in. */
  private separationFromTarget(ctx: MacroContext): number {
    const target = this.target!;
    if (ctx.body.id === target.id) return ctx.ship.body.position.length();
    const targetPosition = ctx.predictor.system.frames.transformPosition(
      new Vec3(),
      target.inertialFrameId,
      ctx.body.inertialFrameId,
      ctx.t,
    );
    return targetPosition.distanceTo(ctx.ship.body.position);
  }
}
